import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import {
  ALL_SAMPLE_ACCEPTANCE_DECISIONS,
  DECISIONS_REQUIRING_REASON,
} from '@lims/shared-types';
import { samplesApi } from '../../api/endpoints.js';
import type { Sample } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  Dialog,
  Panel,
  RadioField,
  StatusBadge,
  TextAreaField,
  useToast,
} from '../../components/ui/index.js';
import { formatDateTime } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { toneForSampleStatus } from './status.js';

/**
 * What the laboratory decided about this sample, and what it decided before.
 *
 * ===========================================================================
 * ACCEPTANCE IS A HISTORY, NOT A FIELD (decision D73).
 * ===========================================================================
 *
 * A held sample commonly becomes accepted once the client supplies what was
 * missing. That is a SEQUENCE of decisions, each with a person, a moment and a
 * reason — not one value overwritten. So this panel shows every decision ever
 * recorded, oldest first, and recording a new one appends: the earlier decision
 * and the reason it was taken stay readable forever, and the database itself
 * refuses an update or a delete.
 *
 * A REASON IS REQUIRED TO HOLD OR REJECT, and the form says so before the
 * button is pressed. A client who is told their sample was refused is owed the
 * reason, and a supervisor reviewing the refusal needs to read it.
 *
 * DECIDING IS A SEPARATE PERMISSION FROM RECEIVING. Somebody without it sees
 * the history and no controls — and the backend refuses the call regardless,
 * which is the part that actually enforces it (CLAUDE.md section 5.1).
 */
export function SampleDecisionPanel({
  sample,
  openRequest = 0,
}: {
  readonly sample: Sample;
  /**
   * Raised by the page to open the decision dialog from elsewhere (the
   * "next steps" after receiving). Each increase opens it once; the person
   * still chooses — nothing is decided for them.
   */
  readonly openRequest?: number;
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const locale = currentLocale();

  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    if (openRequest > 0) setIsOpen(true);
  }, [openRequest]);
  const [decision, setDecision] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const decisions = useQuery({
    queryKey: ['sample', sample.id, 'decisions'],
    queryFn: () => samplesApi.decisions(sample.id),
  });

  const record = useMutation({
    mutationFn: () =>
      samplesApi.decide(sample.id, {
        decision,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sample', sample.id] });
      await queryClient.invalidateQueries({ queryKey: ['samples'] });
      show({ tone: 'success', message: t('samples.decisionRecorded') });
      setIsOpen(false);
      setDecision(null);
      setReason('');
      setNotes('');
    },
  });

  const needsReason =
    decision !== null &&
    (DECISIONS_REQUIRING_REASON as readonly string[]).includes(decision);
  const mayDecide = can(PERMISSIONS.SAMPLES_DECIDE);

  return (
    <Panel
      title={t('samples.decisionTitle')}
      description={t('samples.decisionHint')}
      actions={
        mayDecide && !sample.isDisposed && !sample.isVoided ? (
          <ActionButton priority="secondary" size="sm" onPress={() => setIsOpen(true)}>
            {t('samples.recordDecision')}
          </ActionButton>
        ) : null
      }
    >
      {/*
        Said plainly rather than left to be inferred from a badge colour.
        "Accepted" is a statement about the paperwork and the condition of the
        material, never a statement that the material passed a test — nothing
        has been tested yet at this point.
      */}
      <Alert
        tone={sample.mayBeAssignedTests ? 'info' : 'warning'}
        title={
          sample.mayBeAssignedTests
            ? t('samples.mayBeTestedTitle')
            : t('samples.mayNotBeTestedTitle')
        }
        description={
          sample.mayBeAssignedTests
            ? t('samples.mayBeTestedBody')
            : t('samples.mayNotBeTestedBody')
        }
      />

      {decisions.isLoading ? (
        <p className="mt-stack text-body text-default-600">
          {t('common.loading')}
        </p>
      ) : decisions.error ? (
        <p className="mt-stack text-body text-warning-600">
          {t('samples.decisionsUnavailable')}
        </p>
      ) : (decisions.data ?? []).length === 0 ? (
        // Not "accepted by default": nobody has decided, and the panel says so.
        <p className="mt-stack text-body text-default-600">
          {t('samples.noDecisionYet')}
        </p>
      ) : (
        <ol className="mt-stack flex flex-col gap-stack">
          {(decisions.data ?? []).map((entry) => (
            <li
              key={entry.id}
              className="min-w-0 border-s-2 border-default-200 ps-3"
            >
              <StatusBadge
                label={t(`status.sample.${entry.decision}`, {
                  defaultValue: entry.decision,
                })}
                tone={toneForSampleStatus(entry.decision)}
                size="sm"
              />
              <p className="mt-1 text-caption text-default-600">
                {entry.decidedBy.displayName ?? t('common.notRecorded')}
              </p>
              <p className="text-caption text-default-600">
                {formatDateTime(entry.decidedAt, { locale })}
              </p>
              {entry.reason ? (
                <p className="mt-1 text-body text-default-800">{entry.reason}</p>
              ) : null}
              {entry.notes ? (
                <p className="text-caption text-default-600">{entry.notes}</p>
              ) : null}
            </li>
          ))}
        </ol>
      )}

      <p className="mt-stack text-caption text-default-600">
        {t('samples.decisionAppendOnlyNotice')}
      </p>

      <Dialog
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={t('samples.recordDecision')}
        footer={
          <ActionBar>
            <ActionButton
              priority="tertiary"
              onPress={() => setIsOpen(false)}
              isDisabled={record.isPending}
            >
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              // Disabled where the rule is already known to be unmet, so the
              // reason is asked for before a round trip. The backend refuses it
              // in the same case regardless.
              isDisabled={!decision || (needsReason && !reason.trim())}
              isLoading={record.isPending}
              onPress={() => record.mutate()}
            >
              {t('samples.recordDecision')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="flex flex-col gap-stack">
          {/*
            The four decisions side by side, each with what it means for the
            lab work (D278, the acceptance desk) — chosen deliberately, never
            preselected. The same four values, the same rule for the reason.
          */}
          <RadioField
            label={t('samples.decisionLabel')}
            value={decision}
            onChange={setDecision}
            isRequired
            options={ALL_SAMPLE_ACCEPTANCE_DECISIONS.map((value) => ({
              value,
              label: t(`status.sample.${value}`),
              description: t(`samples.decisionOption.${value}`),
            }))}
          />
          <TextAreaField
            label={t('common.reason')}
            description={
              needsReason
                ? t('samples.reasonRequiredHint')
                : t('samples.reasonOptionalHint')
            }
            value={reason}
            onChange={setReason}
            isRequired={needsReason}
            rows={3}
          />
          <TextAreaField
            label={t('samples.decisionNotes')}
            value={notes}
            onChange={setNotes}
            rows={2}
          />
          {record.error ? (
            <Alert tone="danger" title={t('samples.decisionFailed')} />
          ) : null}
        </div>
      </Dialog>
    </Panel>
  );
}
